import { useEffect, useState } from 'react';
import { paginateQuery } from '../services/databaseService';

/**
 * Server-side pagination for a completed query result.
 *
 * The backend returns page 1 of the result plus a `pagination` block with the
 * total count and a `process_id` that the /paginate endpoint uses to fetch any
 * other page (re-running the original SQL with OFFSET/FETCH server-side). This
 * hook drives that: it never holds more than one page in memory, so a 50,000-row
 * answer is fully browsable without shipping 50,000 rows to the browser.
 *
 * Structural query type (kept minimal so this hook doesn't import — and cycle
 * with — the component that defines QueryCardData).
 */
export interface PaginatableQuery {
  process_id?: string;
  status?: string;
  pagination?: {
    process_id?: string;
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  } | null;
}

export interface ServerPaginationState {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  /** Rows for the current page, or null to fall back to the query's page-1 data. */
  pageData: Record<string, unknown>[] | null;
  isLoadingPage: boolean;
  pageError: string | null;
  /** True when there is more than one page and we have what we need to fetch pages. */
  canPaginate: boolean;
  handlePageChange: (newPage: number, newPageSize?: number) => Promise<void>;
}

export function useServerPagination(
  query: PaginatableQuery,
  pageSessionKey?: string,
): ServerPaginationState {
  const init = query.pagination;
  const [currentPage, setCurrentPage] = useState(init?.page ?? 1);
  const [pageSize, setPageSize] = useState(init?.page_size ?? 10);
  const [totalCount, setTotalCount] = useState(init?.total_count ?? 0);
  const [totalPages, setTotalPages] = useState(init?.total_pages ?? 1);
  const [pageData, setPageData] = useState<Record<string, unknown>[] | null>(null);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  // Reset when a new query result arrives (new process / status transition).
  useEffect(() => {
    if (query.pagination) {
      setCurrentPage(query.pagination.page);
      setPageSize(query.pagination.page_size);
      setTotalCount(query.pagination.total_count);
      setTotalPages(query.pagination.total_pages);
    }
    setPageData(null);
    setPageError(null);
  }, [query.process_id, query.status]);

  const handlePageChange = async (newPage: number, newPageSize?: number) => {
    const effectivePageSize = newPageSize ?? pageSize;
    const processId = query.pagination?.process_id ?? query.process_id;
    if (!pageSessionKey || !processId) return;

    setIsLoadingPage(true);
    setPageError(null);
    try {
      const res = await paginateQuery({
        page_session_key: pageSessionKey,
        process_id: processId,
        page: newPage,
        page_size: effectivePageSize,
      });
      setPageData(res.data);
      setCurrentPage(res.page);
      setPageSize(res.page_size);
      setTotalCount(res.total_count);
      setTotalPages(res.total_pages);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to load page');
    } finally {
      setIsLoadingPage(false);
    }
  };

  const canPaginate = Boolean(
    pageSessionKey && (query.pagination?.process_id ?? query.process_id) && totalPages > 1,
  );

  return {
    currentPage,
    pageSize,
    totalCount,
    totalPages,
    pageData,
    isLoadingPage,
    pageError,
    canPaginate,
    handlePageChange,
  };
}
